// พิมพ์กระดาษคำตอบของทั้งห้อง — หนึ่งหน้าต่อนักเรียนหนึ่งคน ชื่อและรหัสแผ่นพิมพ์มาให้แล้ว
//
// ต้องพิมพ์ขนาดจริง (สเกล 100%) — ตัวอ่านกระดาษจัดภาพจากมุมสี่เหลี่ยม จึงทนการย่อได้บ้าง
// แต่ "ย่อให้พอดีหน้า" ของบางเครื่องตัดมุมทิ้ง แล้วแผ่นนั้นจะอ่านไม่ได้เลย
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Button, Form } from 'react-bootstrap'
import { Link, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import { useReactToPrint } from 'react-to-print'
import axiosInstance from '../services/axiosInterceptor'
import useSchoolSettings from '../hooks/useSchoolSettings'
import { API, roomLabel, showError } from './examUtils'
import AnswerSheet from './AnswerSheet'
import { bubbleLayout, capacityOf, frameFor, HALF_VERSION, LARGE_VERSION, LARGE_HALF_VERSION, LAYOUT_VERSION, objectiveCount, sheetVersionFor } from './sheetLayout'
import { encodeSheetCode } from './sheetCode'
import { spareHalfPng, PNG_DPI } from './sheetImage'
import '../print.css'

// กระดาษสำรองต่อท้ายชุดของห้อง — กันแผ่นจริงหาย/เปื้อน/นักเรียนย้ายเข้าหลังพิมพ์
// ไม่ผูกกับใคร นักเรียนเขียนชื่อและฝนเลขประจำตัวเอง ครูยืนยันทุกแผ่นตอนสแกน (ดู AnswerSheet / reader)
const MAX_SPARE = 50
// โหมด "กระดาษสำรองอย่างเดียว" — ครูให้นักเรียนทั้งห้องฝนเลขประจำตัวเอง (ไม่ต้องแจกตามชื่อ) จึงต้องพิมพ์ได้ทั้งห้อง
const MAX_SPARE_ONLY = 120
// คำชี้แจงกระดาษคำตอบสำหรับผู้คุมสอบ (A4 หนึ่งแผ่น) — แก้แบบกระดาษเมื่อไหร่ต้องแก้ต้นฉบับแล้วออก PDF ใหม่
const PROCTOR_GUIDE = `${process.env.PUBLIC_URL}/docs/exam-proctor-guide.pdf`

const PAGE_STYLE = `
  @page { size: A4 portrait; margin: 0; }
  html, body { margin: 0; padding: 0; background: #fff; }
  /* สูงน้อยกว่า A4 นิดหนึ่ง — สูงเท่ากันพอดี เศษของการปัดทำให้ได้หน้าขาวคั่นทุกแผ่น (ขอบล่างว่างอยู่แล้ว) */
  .exam-page { margin: 0 !important; box-shadow: none !important; height: 296mm; overflow: hidden;
               page-break-after: always; break-after: page; }
  .exam-page:last-child { page-break-after: auto; break-after: auto; }
  .exam-sheet { display: block; }
`

const inactive = (s) => String(s.st_status) === '0' || s.transfer_room_id != null

export default function ExamPrint() {
  const { examId, teachId } = useParams()
  const school = useSchoolSettings()
  const [data, setData] = useState(null)
  const [roster, setRoster] = useState([])
  const [onlyMissing, setOnlyMissing] = useState(false)
  const [spareCount, setSpareCount] = useState(2)
  const [spareOnly, setSpareOnly] = useState(false)
  const [writing, setWriting] = useState('lines')
  // วงใหญ่ (แบบ 6) — ครูเลือกเอง เต็มหน้าเสมอ รับข้อได้น้อยกว่า ตัวอ่านแยกแบบจากแถบรหัสเอง
  const [large, setLarge] = useState(false)
  // เต็มหน้าแม้ข้อพอครึ่งแผ่น — ไม่เลือก = ครึ่งแผ่นเมื่อข้อพอ (รายคนและสำรองคิดความจุแยกกัน)
  const [full, setFull] = useState(false)
  const [exporting, setExporting] = useState(false)
  const contentRef = useRef(null)

  useEffect(() => {
    Promise.all([
      axiosInstance.get(`${API}/exams/${examId}`),
      axiosInstance.get(`${API}/exams/${examId}/rooms/${teachId}`),
    ])
      .then(([exam, rows]) => {
        setData(exam.data)
        setRoster(rows.data)
      })
      .catch((err) => showError(err, 'โหลดข้อมูลไม่สำเร็จ'))
  }, [examId, teachId])

  const room = data?.rooms.find((r) => r.teach_id === Number(teachId))
  const students = useMemo(
    () => roster.filter((s) => !inactive(s) && (!onlyMissing || !s.answers)),
    [roster, onlyMissing]
  )

  const print = useReactToPrint({
    contentRef,
    documentTitle: data && room ? `กระดาษคำตอบ ${data.exam.exam_name} ${roomLabel(room)}` : 'กระดาษคำตอบ',
    pageStyle: PAGE_STYLE,
  })

  if (!data || !room) return <div className="p-4">กำลังโหลด…</div>

  const activeCount = roster.filter((s) => !inactive(s)).length
  const maxSpare = spareOnly ? MAX_SPARE_ONLY : MAX_SPARE
  // สลับโหมดแล้วตั้งจำนวนให้พอดีกับห้อง (สำรองอย่างเดียว = ทั้งห้อง + 2 เผื่อฝนผิด) ครูแก้ต่อได้
  const changeMode = (only) => {
    setSpareOnly(only)
    setSpareCount(only ? Math.min(MAX_SPARE_ONLY, activeCount + 2) : 2)
  }
  const sheets = spareOnly ? [] : students

  const choices = data.exam.exam_choices
  const largeCap = capacityOf(LARGE_VERSION, choices)
  // ข้อเกินความจุของวงใหญ่ = เลือกไม่ได้ (ปิดตัวเลือกไว้) ไม่ใช่พิมพ์วงปกติแทนเงียบๆ
  const largeFits = objectiveCount(data.questions) <= largeCap
  const useLarge = large && largeFits

  // แบบที่จะพิมพ์ของแผ่นรายคน / แผ่นสำรอง — สำรองรับข้อได้น้อยกว่าหนึ่งคอลัมน์ จึงอาจเต็มหน้าทั้งที่แผ่นรายคนครึ่งแผ่น
  const version = sheetVersionFor(data.questions, choices, false, useLarge, full)
  const spareVersion = sheetVersionFor(data.questions, choices, true, useLarge, full)
  let sheetError = null
  try {
    // ตรวจก่อนวาด — เกินหนึ่งแผ่นหรือเลขเกินแถบรหัส ให้บอกครู ไม่ใช่หน้าขาวทั้งหน้า
    bubbleLayout(data.questions, choices, false, version)
    for (const s of sheets) encodeSheetCode(data.exam.exam_id, s.study_id)
  } catch (err) {
    sheetError = err.message
  }
  // กระดาษสำรองรับข้อปรนัยได้น้อยกว่า (คอลัมน์สุดท้ายเป็นช่องฝนเลขประจำตัว) — เกินแล้วพิมพ์แผ่นจริงได้ตามปกติ
  let spareError = null
  try {
    bubbleLayout(data.questions, choices, true, spareVersion)
  } catch (err) {
    spareError = err.message
  }
  const spares = sheetError || spareError ? 0 : spareCount
  const half = frameFor(version).half
  const spareHalf = frameFor(spareVersion).half
  // ความจุของขนาดวงที่เลือก — ตัวเลือก "ครึ่งแผ่น" กดได้เมื่อแผ่นหลักของโหมดนี้ (รายคน / สำรอง) พอครึ่งแผ่น
  const [fullV, halfV] = useLarge ? [LARGE_VERSION, LARGE_HALF_VERSION] : [LAYOUT_VERSION, HALF_VERSION]
  const halfCap = capacityOf(halfV, choices, spareOnly)
  const halfFits = objectiveCount(data.questions) <= halfCap
  // วงใหญ่ไม่มีครึ่งแผ่น — แบบ 7 สูง 3/5 ของ A4 เพื่อให้ช่องฝนเลขประจำตัว 10 แถวเป็นวงใหญ่ได้ด้วย
  const partName = useLarge ? '3/5 แผ่น' : 'ครึ่งแผ่น'
  const hasWritten = data.questions.some((q) => q.eq_type === 'written')
  const nothing = !sheets.length && !spares
  // PNG ให้ครูวางใน Word แล้วแต่งครึ่งล่างเอง — เฉพาะกระดาษสำรองครึ่งแผ่น (ทุกแผ่นเหมือนกัน ภาพเดียวพิมพ์ได้ทั้งห้อง)
  const canExportPng = spareOnly && spareHalf && !sheetError && !spareError

  const exportPng = async () => {
    // ครูเปิดภาพใน Word แล้วลากย่อ/ครอบตัดได้ง่าย — เตือนก่อนทุกครั้ง
    // ข้อ 1–2 ห้ามเด็ดขาด (ตัวอ่านจัดภาพจากสี่มุมด้วยสัดส่วนของแบบกระดาษ) ส่วนการย่อทั้งภาพตามสัดส่วนทนได้
    // เพราะ homography จัดขนาดให้ แต่สแกนแบบ PDF หาสี่เหลี่ยมมุมที่ขนาด 0.7–1.6 เท่าของขนาดจริง
    // (reader.findMarkerIn) — ย่อต่ำกว่า 70% แล้วหามุมไม่เจอ ขอบปกติของ Word (2.54 ซม.) ย่อเหลือ 76% เฉียดเกินไป
    const ok = await Swal.fire({
      icon: 'warning',
      title: 'ก่อนนำภาพไปใช้ใน Word',
      html: `<div class="text-start">
        <ol class="mb-2">
          <li><b>ห้ามบีบหรือยืดภาพให้ผิดสัดส่วน</b> (ย่อ/ขยายต้องลากที่มุมภาพ ไม่ใช่ขอบด้านข้าง)</li>
          <li><b>ห้ามครอบตัด</b> สี่เหลี่ยมดำทั้งสี่มุมและแถบดำขอบบน/ล่างต้องอยู่ครบ</li>
          <li><b>ย่อได้เล็กน้อย ไม่ต่ำกว่า 80%</b> — ตั้งขอบกระดาษใน Word เป็นแบบ<b>แคบ (1.27 ซม.)</b>
            ภาพจะย่อเหลือราว 88% ยังสแกนได้ แต่ขอบแบบปกติ (2.54 ซม.) ย่อเหลือ 76% เล็กเกินไป</li>
        </ol>
        ทำผิดข้อ 1 หรือ 2 กระดาษจะสแกนไม่ได้ — ก่อนถ่ายเอกสารทั้งห้อง ควรพิมพ์ลองหนึ่งแผ่นแล้วสแกนดูก่อน
      </div>`,
      showCancelButton: true,
      confirmButtonText: 'เข้าใจแล้ว ดาวน์โหลด',
      cancelButtonText: 'ยกเลิก',
    })
    if (!ok.isConfirmed) return
    setExporting(true)
    try {
      const blob = await spareHalfPng({ exam: data.exam, questions: data.questions, room, school, large: useLarge })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `กระดาษสำรอง ${data.exam.exam_name}.png`
      a.click()
      setTimeout(() => URL.revokeObjectURL(a.href), 1000)
    } catch (err) {
      showError(err, 'ส่งออกภาพไม่สำเร็จ')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="p-3" style={{ background: '#e9ecef', minHeight: '100vh' }}>
      <div className="d-print-none mx-auto mb-3" style={{ maxWidth: '210mm' }}>
        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
          <Link to={`/teaching/exams/${examId}`} className="btn btn-light btn-sm">
            <i className="ri-arrow-left-line"></i> กลับ
          </Link>
          <div className="me-auto fw-semibold">
            {data.exam.exam_name} · ห้อง {roomLabel(room)} ·{' '}
            {spareOnly ? `สำรอง ${spares} แผ่น` : `${sheets.length} แผ่น${spares ? ` + สำรอง ${spares}` : ''}`}
          </div>
          <Form.Select size="sm" value={spareOnly ? 'spare' : 'named'} onChange={(e) => changeMode(e.target.value === 'spare')}
            style={{ width: 'auto' }} aria-label="แบบที่จะพิมพ์">
            <option value="named">รายคน (พิมพ์ชื่อให้)</option>
            <option value="spare" disabled={!!spareError}>กระดาษสำรองอย่างเดียว (นักเรียนฝนเลขเอง)</option>
          </Form.Select>
          <Form.Select size="sm" value={useLarge ? 'large' : 'normal'} onChange={(e) => setLarge(e.target.value === 'large')}
            style={{ width: 'auto' }} aria-label="ขนาดวง">
            <option value="normal">วงปกติ</option>
            <option value="large" disabled={!largeFits}>วงใหญ่ (ไม่เกิน {largeCap} ข้อ)</option>
          </Form.Select>
          <Form.Select size="sm" value={full || !halfFits ? 'full' : 'half'} onChange={(e) => setFull(e.target.value === 'full')}
            style={{ width: 'auto' }} aria-label="ขนาดกระดาษ">
            <option value="half" disabled={!halfFits}>{partName} (ไม่เกิน {halfCap} ข้อ)</option>
            <option value="full">เต็มหน้า (ไม่เกิน {capacityOf(fullV, choices, spareOnly)} ข้อ)</option>
          </Form.Select>
          {!spareOnly && (
            <Form.Check type="switch" id="only-missing" label="เฉพาะคนที่ยังไม่มีคำตอบ"
              checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} />
          )}
          <Form.Group className="d-flex flex-shrink-0 align-items-center gap-1" controlId="spare-count">
            <Form.Label className="mb-0 fs-sm text-nowrap flex-shrink-0">{spareOnly ? 'จำนวน' : 'กระดาษสำรอง'}</Form.Label>
            <Form.Control type="number" size="sm" min={0} max={maxSpare} style={{ flex: '0 0 64px', width: 64 }}
              value={spareCount} disabled={!!spareError}
              onChange={(e) => setSpareCount(Math.max(0, Math.min(maxSpare, Math.trunc(Number(e.target.value)) || 0)))} />
            <span className="fs-sm text-nowrap flex-shrink-0">แผ่น</span>
          </Form.Group>
          {(spareOnly ? spareHalf : half) && hasWritten && (
            <Form.Group className="d-flex flex-shrink-0 align-items-center gap-1" controlId="writing">
              <Form.Label className="mb-0 fs-sm text-nowrap flex-shrink-0">ครึ่งล่าง</Form.Label>
              <Form.Select size="sm" value={writing} onChange={(e) => setWriting(e.target.value)} style={{ width: 'auto' }}>
                <option value="lines">มีเส้นบรรทัด</option>
                <option value="blank">ว่าง</option>
              </Form.Select>
            </Form.Group>
          )}
          {/* คำชี้แจงให้ผู้คุมสอบอ่านให้นักเรียนฟัง — ต้นฉบับ docs/exam/proctor-answer-sheet-guide.html */}
          <Button size="sm" variant="outline-secondary" href={PROCTOR_GUIDE} target="_blank" rel="noopener noreferrer">
            <i className="ri-file-text-line"></i> คำชี้แจง
          </Button>
          {canExportPng && (
            <Button size="sm" variant="outline-primary" disabled={exporting} onClick={exportPng}>
              <i className="ri-image-line"></i> {exporting ? 'กำลังสร้างภาพ…' : 'ดาวน์โหลด PNG'}
            </Button>
          )}
          <Button size="sm" disabled={nothing || !!sheetError} onClick={print}>
            <i className="ri-printer-line"></i> พิมพ์
          </Button>
        </div>
        {sheetError && <Alert variant="danger" className="py-2 fs-sm">{sheetError}</Alert>}
        {!sheetError && spareError && (
          <Alert variant="warning" className="py-2 fs-sm">{spareError} — ข้อสอบนี้พิมพ์กระดาษสำรองไม่ได้</Alert>
        )}
        {spareOnly && !spareError && (
          <Alert variant="warning" className="py-2 fs-sm">
            ทุกแผ่นไม่มีชื่อ นักเรียนเขียนชื่อและฝนเลขประจำตัวเอง — <b>ตอนสแกนครูต้องดูชื่อที่เขียนแล้วยืนยันทีละแผ่น</b>
            ระบบไม่บันทึกเองแม้เลขที่ฝนตรงกับนักเรียนคนเดียว (เลขที่ฝนไม่มีรหัสตรวจสอบ ฝนผิดหลักเดียวอาจตรงกับเพื่อน)
            จึงช้ากว่ากระดาษรายคน
          </Alert>
        )}
        {!sheetError && !(spareOnly && spareError) && (
          <Alert variant="light" className="py-2 fs-sm border">
            {useLarge && <><b>วงใหญ่</b> (เส้นผ่านศูนย์กลาง 6 มม.) ฝนง่ายและสแกนแม่นกว่า · </>}
            {(spareOnly ? spareHalf : half)
              ? <>พิมพ์<b>{partName}บน</b> ส่วนล่างและด้านหลังให้นักเรียนตอบ{hasWritten ? 'ข้ออัตนัย' : 'หรือทดเลข'}</>
              : <>พิมพ์<b>เต็มหน้า</b>{hasWritten ? ' ข้ออัตนัยตอบในกระดาษแยก' : ''}</>}
            {!spareOnly && spares > 0 && half && !spareHalf
              ? ` (กระดาษสำรองพิมพ์เต็มหน้า — ${partName} รับได้ไม่เกิน ${capacityOf(halfV, choices, true)} ข้อ)`
              : ''}
          </Alert>
        )}
        {canExportPng && (
          <Alert variant="light" className="py-2 fs-sm border">
            <b>ดาวน์โหลด PNG</b> = ภาพ{partName}บน ({PNG_DPI} dpi กว้าง 21 ซม.) ไว้วางใน Word แล้วพิมพ์โจทย์/ข้อความส่วนล่างเอง —
            ใน Word ตั้งขอบกระดาษแบบ<b>แคบ (1.27 ซม.)</b> ภาพจะย่อเหลือราว 88% ยังสแกนได้ (ย่อได้ไม่ต่ำกว่า 80%) ·
            <b>ห้ามยืด/บีบภาพ ห้ามครอบตัด</b> สี่เหลี่ยมดำทั้งสี่มุมและแถบดำขอบบน/ล่างต้องอยู่ครบ
          </Alert>
        )}
        <Alert variant="info" className="py-2 fs-sm mb-0">
          ตั้งค่าเครื่องพิมพ์เป็นกระดาษ A4 ขนาดจริง (สเกล 100%) ไม่ใช่ "ย่อให้พอดีหน้า" —
          สี่เหลี่ยมดำสี่มุมต้องอยู่ครบทุกแผ่น ระบบใช้จัดภาพตอนสแกน · กระดาษแต่ละแผ่นผูกกับนักเรียนคนนั้น ห้ามสลับกัน ·
          กระดาษสำรองใช้แทนแผ่นที่หาย/เปื้อน นักเรียนเขียนชื่อและฝนเลขประจำตัวเอง ตอนสแกนครูต้องยืนยันชื่อทุกแผ่น
        </Alert>
      </div>

      {!sheetError && (
        <div ref={contentRef}>
          {sheets.map((s) => (
            <div key={s.study_id} className="exam-page mx-auto mb-3 shadow-sm" style={{ width: '210mm', background: '#fff' }}>
              <AnswerSheet exam={data.exam} questions={data.questions} student={s} room={room} school={school} writing={writing} large={useLarge} full={full} />
            </div>
          ))}
          {Array.from({ length: spares }, (_, i) => (
            <div key={`spare-${i}`} className="exam-page mx-auto mb-3 shadow-sm" style={{ width: '210mm', background: '#fff' }}>
              <AnswerSheet exam={data.exam} questions={data.questions} room={room} school={school} spare writing={writing} large={useLarge} full={full} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
